import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle, Mail, Briefcase, Newspaper, MapPin, Building2,
  AlertCircle, ArrowRight,
} from "lucide-react";

/* ─── TOKENS ─── */
const C = {
  white:   "#FFFFFF",
  offWhite:"#F8F7F5",
  cream:   "#F2F0EC",
  black:   "#0A0A0A",
  dark:    "#1A1A1A",
  gray:    "#6B6B6B",
  grayLt:  "#D4D4D4",
  border:  "#E5E3DF",
  red:     "#E50914",
  redDark: "#B00710",
  redLt:   "#FFF0F0",
  sage:    "#4A7C59",
};

/* ─── Font ─── */
const fontHref = "https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&family=DM+Sans:wght@300;400;500;600;700&display=swap";
if (typeof document !== "undefined" && !document.head.querySelector(`link[href="${fontHref}"]`)) {
  const l = document.createElement("link"); l.rel = "stylesheet"; l.href = fontHref;
  document.head.appendChild(l);
}

const API = "https://europe-west3-4reva.cloudfunctions.net/ContactUs";

const TOPICS = [
  "General Enquiry",
  "Partnership Opportunity",
  "Media & Press",
  "Investment",
  "Careers",
  "Legal & Compliance",
  "Other",
];

const CONTACT_INFO = [
  { icon: Mail,      label: "General",   value: "contact@4reva.com" },
  { icon: Briefcase, label: "Careers",   value: "careers@4reva.com" },
  { icon: Newspaper, label: "Press",     value: "press@4reva.com" },
  { icon: MapPin,    label: "Location",  value: "Tallinn, Estonia, EU" },
  { icon: Building2, label: "Reg. No.", value: "17381555" },
];

/* ════════════════════════════════════
   CONTACT US PAGE
   ════════════════════════════════════ */
export default function ContactUs() {
  const navigate = useNavigate();

  const [form, setForm] = useState({
    firstName: "",
    lastName:  "",
    email:     "",
    phone:     "",
    company:   "",
    topic:     "",
    message:   "",
  });
  const [focus,   setFocus]   = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [sent,    setSent]    = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) =>
    setForm(f => ({ ...f, [k]: e.target.value }));

  const inp = (field: string): React.CSSProperties => ({
    width: "100%", boxSizing: "border-box" as const,
    background: C.white,
    border: `1.5px solid ${focus === field ? C.red : C.border}`,
    padding: "13px 16px",
    color: C.black,
    fontSize: 14,
    fontFamily: "'DM Sans', sans-serif",
    outline: "none",
    borderRadius: 0,
    transition: "border-color 0.2s",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSending(true);
    setError(null);

    try {
      const res = await fetch(API, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          type:      "contact",
          firstName: form.firstName,
          lastName:  form.lastName,
          email:     form.email,
          phone:     form.phone,
          company:   form.company,
          topic:     form.topic,
          message:   form.message,
        }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSent(true);
    } catch (err: any) {
      setError(err.message || "Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  /* ── Success ── */
  if (sent) {
    return (
      <div style={{ minHeight: "100vh", background: C.offWhite, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Sans', sans-serif", padding: 24 }}>
        <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
          style={{ background: C.white, maxWidth: 520, width: "100%", padding: "60px 48px", textAlign: "center", borderTop: `4px solid ${C.red}` }}>
          <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ delay: 0.3, type: "spring" }}
            style={{ width: 72, height: 72, background: C.red, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 28px", color: C.white }}>
            <CheckCircle size={36} strokeWidth={2} />
          </motion.div>
          <h2 style={{ fontFamily: "'DM Serif Display', serif", fontSize: 32, fontWeight: 400, color: C.black, margin: "0 0 16px" }}>Message Received</h2>
          <p style={{ fontSize: 15, lineHeight: 1.8, color: C.gray, margin: "0 0 36px" }}>
            Thank you, <strong style={{ color: C.black }}>{form.firstName}</strong>. We'll get back to you within 2 business days.
          </p>
          <div style={{ display: "flex", gap: 12, justifyContent: "center", flexWrap: "wrap" }}>
            <motion.button whileHover={{ scale: 1.02 }} onClick={() => navigate("/")}
              style={{ background: C.red, color: C.white, border: "none", padding: "13px 32px", fontWeight: 700, fontSize: 13, letterSpacing: "0.14em", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
              GO HOME
            </motion.button>
            <motion.button whileHover={{ scale: 1.02 }} onClick={() => { setSent(false); setForm({ firstName:"", lastName:"", email:"", phone:"", company:"", topic:"", message:"" }); }}
              style={{ background: "transparent", color: C.black, border: `1.5px solid ${C.border}`, padding: "13px 32px", fontWeight: 600, fontSize: 13, letterSpacing: "0.14em", cursor: "pointer", fontFamily: "'DM Sans', sans-serif" }}>
              SEND ANOTHER
            </motion.button>
          </div>
        </motion.div>
      </div>
    );
  }

  /* ── Page ── */
  return (
    <>
      <Helmet>
        <title>Contact Us </title>
        <meta name="description" content="Get in touch with 4REVA GROUP. Partnerships, press, careers and general enquiries." />
      </Helmet>

      <div style={{ minHeight: "100vh", background: C.offWhite, fontFamily: "'DM Sans', sans-serif" }}>

        {/* ── NAV ── */}
        <nav style={{ background: C.white, borderBottom: `1px solid ${C.border}`, padding: "0 6%", height: 64, display: "flex", alignItems: "center", justifyContent: "space-between", position: "sticky", top: 0, zIndex: 100 }}>
          <motion.div whileHover={{ scale: 1.02 }} onClick={() => navigate("/")} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 30, height: 30, background: C.red, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'DM Serif Display', serif", fontSize: 12, color: C.white }}>SG</div>
            <span style={{ fontFamily: "'DM Serif Display', serif", fontSize: 15, letterSpacing: "0.2em", color: C.black }}>4REVA GROUP</span>
          </motion.div>
          <button onClick={() => navigate(-1)}
            style={{ background: "none", border: `1px solid ${C.border}`, color: C.gray, fontSize: 13, cursor: "pointer", fontFamily: "'DM Sans', sans-serif", padding: "8px 18px", letterSpacing: "0.06em", transition: "all 0.2s" }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = C.black; e.currentTarget.style.color = C.black; }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = C.border; e.currentTarget.style.color = C.gray; }}>
            ← Back
          </button>
        </nav>

        {/* ── Hero strip ── */}
        <div style={{ background: C.black, padding: "60px 6% 56px", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", inset: 0, backgroundImage: "linear-gradient(rgba(229,9,20,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(229,9,20,0.05) 1px, transparent 1px)", backgroundSize: "60px 60px" }} />
          <div style={{ position: "absolute", top: 0, left: 0, width: 3, height: "100%", background: `linear-gradient(to bottom, ${C.red}, transparent)` }} />
          <div style={{ maxWidth: 900, margin: "0 auto", position: "relative" }}>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.1 }}
              style={{ fontSize: 11, letterSpacing: "0.4em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 14px" }}>
              Reach Out
            </motion.p>
            <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
              style={{ fontFamily: "'DM Serif Display', serif", fontSize: "clamp(32px, 5vw, 56px)", fontWeight: 400, color: C.white, margin: "0 0 16px", lineHeight: 1.1 }}>
              Let's start a conversation.
            </motion.h1>
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.35 }}
              style={{ fontSize: 15, color: "rgba(255,255,255,0.5)", margin: 0, maxWidth: 500, lineHeight: 1.7 }}>
              Whether it's a partnership, a press enquiry, or just a hello — we read every message personally.
            </motion.p>
          </div>
        </div>

        {/* ── Content ── */}
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "60px 6% 100px", display: "grid", gridTemplateColumns: "1fr 340px", gap: 40, alignItems: "start" }}>

          {/* ── FORM ── */}
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <form onSubmit={handleSubmit}>

              {/* Personal */}
              <div style={{ background: C.white, padding: "36px", marginBottom: 4, borderTop: `3px solid ${C.red}` }}>
                <p style={{ fontSize: 10, letterSpacing: "0.3em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 24px" }}>Your Details</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Lbl label="First Name" required>
                      <input required value={form.firstName} onChange={set("firstName")}
                        onFocus={() => setFocus("firstName")} onBlur={() => setFocus(null)}
                        placeholder="First name" style={inp("firstName")} />
                    </Lbl>
                    <Lbl label="Last Name" required>
                      <input required value={form.lastName} onChange={set("lastName")}
                        onFocus={() => setFocus("lastName")} onBlur={() => setFocus(null)}
                        placeholder="Last name" style={inp("lastName")} />
                    </Lbl>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                    <Lbl label="Email Address" required>
                      <input required type="email" value={form.email} onChange={set("email")}
                        onFocus={() => setFocus("email")} onBlur={() => setFocus(null)}
                        placeholder="you@example.com" style={inp("email")} />
                    </Lbl>
                    <Lbl label="Phone Number">
                      <input type="tel" value={form.phone} onChange={set("phone")}
                        onFocus={() => setFocus("phone")} onBlur={() => setFocus(null)}
                        placeholder="+372 000 0000" style={inp("phone")} />
                    </Lbl>
                  </div>
                  <Lbl label="Company / Organisation">
                    <input value={form.company} onChange={set("company")}
                      onFocus={() => setFocus("company")} onBlur={() => setFocus(null)}
                      placeholder="Your company name (optional)" style={inp("company")} />
                  </Lbl>
                </div>
              </div>

              {/* Message */}
              <div style={{ background: C.white, padding: "36px", marginBottom: 4, borderTop: `1px solid ${C.border}` }}>
                <p style={{ fontSize: 10, letterSpacing: "0.3em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 24px" }}>Your Message</p>
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  <Lbl label="Topic" required>
                    <select required value={form.topic} onChange={set("topic")}
                      onFocus={() => setFocus("topic")} onBlur={() => setFocus(null)}
                      style={{ ...inp("topic"), appearance: "none" as const, cursor: "pointer", color: form.topic ? C.black : C.gray }}>
                      <option value="" disabled>Select a topic</option>
                      {TOPICS.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Lbl>
                  <Lbl label="Message" required>
                    <textarea required value={form.message} onChange={set("message")}
                      onFocus={() => setFocus("message")} onBlur={() => setFocus(null)}
                      placeholder="Tell us how we can help you..."
                      rows={6} style={{ ...inp("message"), resize: "vertical" as const }} />
                  </Lbl>
                </div>
              </div>

              {/* Error */}
              <AnimatePresence>
                {error && (
                  <motion.div initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
                    style={{ padding: "14px 18px", background: C.redLt, border: `1px solid ${C.red}30`, fontSize: 13, color: C.red, marginBottom: 16, display: "flex", alignItems: "center", gap: 10 }}>
                    <AlertCircle size={16} />
                    {error}
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Submit */}
              <motion.button type="submit" disabled={sending}
                whileHover={{ scale: sending ? 1 : 1.01 }} whileTap={{ scale: sending ? 1 : 0.98 }}
                style={{ width: "100%", background: sending ? C.grayLt : C.red, color: sending ? C.gray : C.white, border: "none", padding: "18px 0", fontWeight: 700, fontSize: 14, letterSpacing: "0.16em", cursor: sending ? "wait" : "pointer", fontFamily: "'DM Sans', sans-serif", transition: "all 0.2s" }}>
                {sending ? "SENDING…" : "SEND MESSAGE"}
              </motion.button>

              <p style={{ fontSize: 12, color: C.gray, textAlign: "center", marginTop: 14, lineHeight: 1.6 }}>
                We typically respond within 2 business days.
              </p>
            </form>
          </motion.div>

          {/* ── SIDEBAR ── */}
          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.35 }}
            style={{ position: "sticky", top: 84 }}>

            {/* Contact info */}
            <div style={{ background: C.white, padding: "32px", marginBottom: 4, borderTop: `3px solid ${C.red}` }}>
              <p style={{ fontSize: 10, letterSpacing: "0.3em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 24px" }}>Contact Info</p>
              {CONTACT_INFO.map(x => (
                <div key={x.label} style={{ display: "flex", gap: 14, alignItems: "flex-start", marginBottom: 20 }}>
                  <x.icon size={16} color={C.red} style={{ marginTop: 2, flexShrink: 0 }} />
                  <div>
                    <p style={{ fontSize: 10, letterSpacing: "0.14em", color: C.gray, fontWeight: 600, textTransform: "uppercase", margin: "0 0 3px" }}>{x.label}</p>
                    <p style={{ fontSize: 13, color: C.black, margin: 0 }}>{x.value}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Legal */}
            <div style={{ background: C.white, padding: "28px 32px", borderTop: `1px solid ${C.border}` }}>
              <p style={{ fontSize: 10, letterSpacing: "0.3em", color: C.red, fontWeight: 700, textTransform: "uppercase", margin: "0 0 16px" }}>Legal</p>
              <p style={{ fontSize: 12, color: C.gray, lineHeight: 1.7, margin: "0 0 12px" }}>
                4REVA GROUP is incorporated in EU under registration number 17381555, subject to EU regulations.
              </p>
              <a href="https://ariregister.rik.ee/eng/company/17381555/4REVAGROUP-O%C3%9C" target="_blank" rel="noopener noreferrer"
                style={{ fontSize: 12, color: C.red, textDecoration: "none", fontWeight: 600, letterSpacing: "0.06em" }}>
                View Registration →
              </a>
            </div>

            {/* Quick links */}
            <div style={{ background: C.cream, padding: "24px 32px", marginTop: 4 }}>
              <p style={{ fontSize: 10, letterSpacing: "0.3em", color: C.gray, fontWeight: 700, textTransform: "uppercase", margin: "0 0 14px" }}>Quick Links</p>
              {[
                { label: "Open Roles",    path: "/careers" },
                { label: "Our Companies", path: "/#companies" },
                { label: "Privacy Policy",path: "/privacy" },
                { label: "Terms",         path: "/terms" },
              ].map(l => (
                <div key={l.label} onClick={() => navigate(l.path)}
                  style={{ fontSize: 13, color: C.gray, marginBottom: 10, cursor: "pointer", transition: "color 0.2s", display: "flex", alignItems: "center", gap: 6 }}
                  onMouseEnter={e => (e.currentTarget.style.color = C.red)}
                  onMouseLeave={e => (e.currentTarget.style.color = C.gray)}>
                  {l.label} <ArrowRight size={12} />
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* ── Footer ── */}
        <footer style={{ background: C.black, borderTop: `1px solid rgba(255,255,255,0.06)`, padding: "32px 6%", display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 16 }}>
          <p style={{ fontSize: 12, color: "rgba(255,255,255,0.3)", margin: 0 }}>
            © {new Date().getFullYear()} 4REVA GROUP· Reg. 17381555 · Tallinn, Estonia EU
          </p>
          <div style={{ display: "flex", gap: 20 }}>
            {[{ l: "Privacy", p: "/privacy" }, { l: "Terms", p: "/terms" }].map(x => (
              <span key={x.l} onClick={() => navigate(x.p)} style={{ fontSize: 12, color: "rgba(255,255,255,0.35)", cursor: "pointer", transition: "color 0.2s" }}
                onMouseEnter={e => (e.currentTarget.style.color = C.white)}
                onMouseLeave={e => (e.currentTarget.style.color = "rgba(255,255,255,0.35)")}>
                {x.l}
              </span>
            ))}
          </div>
        </footer>
      </div>

      {/* Responsive */}
      <style>{`
        @media (max-width: 768px) {
          div[style*="grid-template-columns: 1fr 340px"] {
            grid-template-columns: 1fr !important;
          }
          div[style*="position: sticky"] {
            position: static !important;
          }
        }
        @media (max-width: 520px) {
          div[style*="grid-template-columns: 1fr 1fr"] {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>
    </>
  );
}

/* ─── Label wrapper ─── */
function Lbl({ label, children, required }: { label: string; children: React.ReactNode; required?: boolean }) {
  return (
    <div>
      <label style={{ display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", color: "#444", marginBottom: 8, textTransform: "uppercase" }}>
        {label}{required && <span style={{ color: "#E50914", marginLeft: 4 }}>*</span>}
      </label>
      {children}
    </div>
  );
}